/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AvatarGroup,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Icon,
  IconButton,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { pushAndNavigate } from "../../native/SwipeBack";
import { deleteWaitlist, postWaitlist } from "../../api/user/play/PlayExpand";

import { TbDots } from "react-icons/tb";
import { StatsBox } from "../../pages/user/ProfileActivity";
import { DetailsPopover } from "../datagrid/Datagrid";
import { getCourtName, type CourtValue } from "../../functions/CourtName";
import LazyAvatar from "../lazy/LazyAvatar";

import { postSessionToken } from "../../api/admin/Events";
import { getEventLabel } from "../../types/EventTypes";
import useMobile from "../../hooks/useMobile";

interface EventUser {
  id: string;
  display_name: string;
  profile_image: string;
}

function toOrdinal(number: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return number + "th";
  }

  return number + (suffixes[lastDigit] || "th");
}

export const formatDateTime = (date: Date): string => {
  const today = new Date();
  const tomorrow = new Date(today);

  const isToday = date.toDateString() === today.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();
  const longOptions: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    hour: "numeric",
    minute: "numeric",
  };
  const shortOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
  };

  tomorrow.setDate(today.getDate() + 1);
  tomorrow.setHours(12, 30, 0, 0);

  if (isToday) {
    return `Today, ${date.toLocaleTimeString("en-US", shortOptions)}`;
  } else if (isTomorrow) {
    return `Tomorrow, ${date.toLocaleTimeString("en-US", shortOptions)}`;
  } else {
    return date.toLocaleDateString("en-US", longOptions);
  }
};

const EventDetailHostCard = ({
  status,
  time,
  type,
  court,
  level,
  host,
  coaches,
  players,
  description,
  price,
  duration,
  id,
  waitlistPosition,
  distance,
  secondary_type,

  onOpenWaitlist,

  location,
  locationName,
  thumbnail,

  onExtraOptionsOpen,
}: {
  status:
  | "full"
  | "available"
  | "cancelled_by_member"
  | "turn"
  | "finished"
  | "booked"
  | "hosted"
  | "activity"
  | "pending_payment"
  | "waitlist"
  | "cancelled_by_host";
  waitlistExpiry?: Date | null;
  time: Date;
  type: string;
  court: CourtValue;
  level: string;
  coaches: EventUser[];
  host: EventUser;
  players: EventUser[];
  description: string;
  price: number;
  duration: number | string;
  distance?: number;
  secondary_type?: string;
  id: string;

  location?: string;
  locationName?: string;
  thumbnail?: string;

  waitlistPosition?: number;
  onOpenWaitlist: () => void;

  onExtraOptionsOpen?: (v: string) => void;
}) => {
  const [hideDetails, setHideDetails] = useState(true);

  const navigate = useNavigate();

  const [token, setToken] = useState<string | null>(null);

  const isMobile = useMobile(750);

  useQuery(["getSessionToken"], () => postSessionToken(), {
    onSuccess: (res) => {
      setToken(res.token);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const isFull =
    status === "full" || status === "turn" || status === "waitlist";

  const queryClient = useQueryClient();

  const joinWaitlistMutation = useMutation(["joinWaitlist"], postWaitlist, {
    onSuccess: () => {
      onOpenWaitlist();
      queryClient.invalidateQueries(["getVenueEvents"]);
    },
    onError: () => {},
  });

  const leaveWaitlistMutation = useMutation(["leaveWaitlist"], deleteWaitlist, {
    onSuccess: () => {
      queryClient.invalidateQueries(["getVenueEvents"]);
    },
    onError: () => {},
  });

  return (
    <Box
      bg="white"
      borderRadius="10px"
      boxShadow="0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14"
      maxW="1100px"
      borderWidth={status == "activity" ? "0.5px" : "1px"}
      borderColor={status == "activity" ? "#00000033" : "#DCE2EA"}
      overflow="hidden"
    >
      {status == "activity" && (
        <StatsBox games={5} heartRate={5} calories={5} steps={5} distance={distance ?? 0} />
      )}

      {status === "hosted" && (
        <Flex justifyContent="space-between" p="20px 20px 20px 20px">
          <DetailsPopover
            thumbnail={thumbnail ?? ""}
            name={locationName ?? ""}
            location={location ?? ""}
            duration={(duration?.toString() ?? "") + " minutes"}
            price={price}
            hostVersion
          />
          <IconButton
            aria-label="options"
            icon={<Icon w="22px" h="22px" as={TbDots} />}
            color="#171C22"
            variant="unstyled"
            onClick={() => {
              if (onExtraOptionsOpen) onExtraOptionsOpen(id);
            }}
          />
        </Flex>
      )}
      {isFull ? (
        <Center h="38px" w="100%" bg="#F5F7F9">
          <Text variant="subtle" fontSize="13">
            This event is full
          </Text>
        </Center>
      ) : (
        <></>
      )}
      <Box p="20px" userSelect="text">
        <Flex mb="16px" justifyContent="space-between">
          <Flex alignItems="center" gap="4px">
            <Text variant="header" fontSize="15" fontWeight="530">
              Time:
            </Text>
            <Text variant="subtle" fontSize="13" fontWeight="400">
              {formatDateTime(time)}
            </Text>
          </Flex>

          <Flex alignItems="center" gap="4px">
            <Text variant="header" fontSize="15" fontWeight="530">
              Court:
            </Text>
            <Text variant="subtle" fontSize="13" fontWeight="400">
              {getCourtName(court)}
            </Text>
          </Flex>

          <Flex alignItems="center" gap="4px">
            <Text
              variant="header"
              fontSize="15"
              color="#171C22"
              fontWeight="530"
            >
              Type:
            </Text>
            <Text variant="subtle" fontSize="13" fontWeight="400">
              {getEventLabel(type)}
            </Text>
          </Flex>
          <Flex>
            <Text
              variant="header"
              fontSize="15"
              color="#171C22"
              fontWeight="530"
            >
              Secondary Type:
            </Text>
            <Text variant="subtle" fontSize="13" fontWeight="400">
              {getEventLabel(secondary_type ?? "")}
            </Text>
          </Flex>

          <Flex alignItems="center" gap="4px">
            <Text variant="header" fontSize="15" fontWeight="530">
              Level:
            </Text>
            <Text variant="subtle" fontSize="13" fontWeight="400">
              {level}
            </Text>
          </Flex>

          <Flex alignItems="center" gap="4px">
            <Text variant="header" fontSize="15" fontWeight="530">
              Coaches({coaches.length}):{" "}
              {!coaches || coaches.length === 0 ? "None" : ""}
            </Text>
            <AvatarGroup minW="50px">
              {coaches.map((coach: any, index: number) => (
                <>
                  <LazyAvatar
                    position="relative"
                    src={coach.profile_image}
                    key={coach.id}
                    width={26}
                    height={26}
                    border="1px solid #dce2ea"
                    zIndex={coaches.length - index}
                    bg="white"
                    right={
                      index != 0
                        ? `${10 + (index - 1) * 26 - 16 * (index - 1)}px`
                        : "0px"
                    }
                    onClick={() => pushAndNavigate(navigate, `/user/profile?id=${coach.id}`)}
                    _hover={{ cursor: "pointer" }}
                  />
                </>
              ))}
            </AvatarGroup>
          </Flex>

          <Flex alignItems="center" gap="4px">
            <Text variant="header" fontSize="15" fontWeight="530">
              Hosting:
            </Text>
            <LazyAvatar
              src={host.profile_image ?? ""}
              key={host.id}
              width={26}
              height={26}
              border="1px solid #dce2ea"
              onClick={() => pushAndNavigate(navigate, `/user/profile?id=${host.id}`)}
              _hover={{ cursor: "pointer" }}
            />
          </Flex>

          <Flex alignItems="center" gap="4px">
            <Text variant="header" fontSize="15" fontWeight="530">
              Players({players ? players.length : 0}):
            </Text>
            <AvatarGroup minW="50px">
              {players &&
                players.slice(0, isMobile ? 3 : 4).map((player: any, index: number) => {
                  const maxAvatars = isMobile ? 3 : 4
                  return (
                    <>
                      <LazyAvatar
                        position="relative"
                        src={player.profile_image ?? ""}
                        key={player.id}
                        width={26}
                        height={26}
                        border="1px solid #dce2ea"
                        zIndex={Math.min(maxAvatars, players.length) - index}
                        bg="white"
                        right={
                          index != 0
                            ? `${10 + (index - 1) * 26 - 16 * (index - 1)}px`
                            : "0px"
                        }
                        onClick={() =>
                          pushAndNavigate(navigate, `/user/profile?id=${player.id}`)
                        }
                        _hover={{ cursor: "pointer" }}
                      />
                    </>
                  );
                })}
              {players && players.length > (isMobile ? 3 : 4) && (
                <Box
                  position="relative"
                  width={26}
                  height={26}
                  minWidth={26}
                  minHeight={26}
                  borderRadius="50%"
                  bg="#F5F7F9"
                  border="1px solid #dce2ea"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  right={isMobile ? `${10 + 2 * 26 - 16 * 2 - 8}px` : `${10 + 3 * 26 - 16 * 3 - 8}px`}
                  zIndex={0}
                  flexShrink={0}
                >
                  <Text fontSize="10px" fontWeight="600" color="#171C22">
                    +{players.length - (isMobile ? 3 : 4)}
                  </Text>
                </Box>
              )}
            </AvatarGroup>
          </Flex>
        </Flex>
      </Box>

      {status != "hosted" && status != "activity" && (
        <>
          <Divider />

          <Box p="20px" userSelect="text">
            <Text
              variant="subtle"
              fontSize="13"
              //noOfLines={hideDetails ? 1 : 10}
              flexWrap="wrap"
            >
              {hideDetails && description.length > 240
                ? `${description.slice(0, 240)}...`
                : description}
            </Text>
            {description && description.length > 240 && (
              <Button
                variant="link"
                fontSize="13px"
                mb="24px"
                position="relative"
              >
                <Text
                  position="absolute"
                  left="0"
                  top="4px"
                  textDecoration="underline"
                  onClick={() => setHideDetails(!hideDetails)}
                >
                  {hideDetails ? "Read more" : "Hide"}
                </Text>
              </Button>
            )}

            <Flex justifyContent="space-between" alignItems="center">
              <Flex gap="8px" alignItems="center">
                <Text variant="title" fontSize="17px" fontWeight="600">
                  ${price.toFixed(0)}
                </Text>
                <Box bg="#7E8B9A" w="4px" h="4px" borderRadius="2px" />
                <Text variant="title" fontSize="17px" fontWeight="600">
                  {duration} min
                </Text>
              </Flex>
              {status === "waitlist" ? (
                <>
                  <Button
                    variant="light"
                    size="sm"
                    _hover={{}}
                    _active={{}}
                    cursor="not-allowed"
                  >
                    <Text variant="subtle">
                      You're{" "}
                      <span style={{ color: "#06C270" }}>
                        {toOrdinal(waitlistPosition ?? 0)}
                      </span>{" "}
                      on the waitlist
                    </Text>
                  </Button>
                </>
              ) : status === "turn" ? (
                <Flex gap="10px">
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() => leaveWaitlistMutation.mutate({ id: id })}
                    isLoading={leaveWaitlistMutation.isLoading}
                  >
                    Deny
                  </Button>
                  <Button
                    variant="heavy"
                    size="sm"
                    onClick={() =>
                      navigate(`/checkout/play?id=${id}&access_token=${token}`)
                    }
                  >
                    Confirm
                  </Button>
                </Flex>
              ) : status === "finished" ? (
                <Button variant="heavy" size="sm" isDisabled>
                  Finished
                </Button>
              ) : (
                <Link
                  to={
                    status === "available"
                      ? `/checkout/play?id=${id}&access_token=${token}`
                      : ""
                  }
                >
                  <Button
                    variant="heavy"
                    size="sm"
                    onClick={() =>
                      status === "full"
                        ? joinWaitlistMutation.mutate({ id: id })
                        : {}
                    }
                    isDisabled={
                      status === "booked" ||
                      status === "pending_payment" ||
                      status === "cancelled_by_member" ||
                      status === "cancelled_by_host"
                    }
                    isLoading={joinWaitlistMutation.isLoading}
                  >
                    {status === "full"
                      ? "Join waitlist"
                      : status === "booked"
                        ? "Booked"
                        : status == "pending_payment"
                          ? "Pending Payment"
                          : status == "cancelled_by_member"
                            ? "Cancelled by Me"
                            : status == "cancelled_by_host"
                              ? "Cancelled by Host"
                              : "Book Now"}
                  </Button>
                </Link>
              )}
            </Flex>
          </Box>
        </>
      )}
    </Box>
  );
};

export default EventDetailHostCard;
